<template lang="html">
    <div
        ref="coninput"
        :style="styleLabel"
        :class="[
            `vs-input-${color}`,
            {
                'isFocus': isFocus,
                'input-icon-validate-success': success,
                'input-icon-validate-danger': danger || hasValidationErrors,
                'input-icon-validate-warning': warning,
                'is-label-placeholder': labelPlaceholder,
                'with-label': labelPlaceholder ? false : label
            }
        ]"
        class="vs-component vs-con-input-label vs-input"
    >
        <label
            v-if="label && !labelPlaceholder"
            class="vs-input--label"
            :for="`${_uid}-input`"
            @click="focusInput"
            v-html="label_text"
        />
        <div class="vs-con-input">
            <input
                ref="vsinput"
                v-validate="validationRule"
                :id="`${_uid}-input`"
                :name="name"
                :style="style"
                :autofocus="autofocus"
                :disabled="disabled"
                :class="[
                    size,
                    {
                        'hasValue': value,
                        'hasIcon': icon,
                        'icon-after-input': iconAfter
                    }
                ]"
                :accept="accept"
                :placeholder="null"
                :value="value"
                v-bind="$attrs"
                v-on="listeners"
                :type="$attrs.type ? $attrs.type : 'text'"
                class="vs-inputx vs-input--input"
            />

            <transition name="placeholderx">
                <span
                    v-if="isValue && placeholder_text"
                    ref="spanplaceholder"
                    :style="styleLabel"
                    :class="[
                        labelPlaceholder && size,
                        size,
                        {
                            'vs-placeholder-label': labelPlaceholder
                        }
                    ]"
                    class="input-span-placeholder vs-input--placeholder"
                    @click="focusInput"
                    v-html="placeholder_text"
                >
                </span>
            </transition>

            <vs-icon
                v-if="icon"
                :class="{ 'icon-after': iconAfter, 'icon-no-border': iconNoBorder }"
                :icon-pack="iconPack"
                :icon="icon"
                class="icon-inputx notranslate vs-input--icon"
                @click="iconClick()"
            >
            </vs-icon>

            <slot></slot>

            <transition name="icon-validate" v-if="!hideIconValidate">
                <span
                    v-if="
                        hasValidationErrors || (!hideStateIcons && (success || danger || warning))
                    "
                    :class="{
                        'icon-before': iconAfter,
                        'cursor-pointer': danger || hasValidationErrors
                    }"
                    class="input-icon-validate vs-input--icon-validate"
                    @click="clear"
                >
                    <vs-icon
                        :class="{ 'icon-before': iconAfter }"
                        :icon-pack="valIconPack"
                        :icon="getIcon"
                    />
                </span>
                <vx-action-icon
                    v-else-if="value && !disabled && clearable"
                    class="mr-2 clear-button"
                    style="background-color: inherit"
                    icon="XIcon"
                    :color="isFocus ? 'danger' : 'dark'"
                    hover-color="danger"
                    @mousedown.stop.prevent="clear"
                />
            </transition>
        </div>

        <div :class="{ 'validation-padding': !removeValidationPadding }">
            <transition-group @before-enter="beforeEnter" @enter="enter" @leave="leave">
                <div
                    v-if="hasValidationErrors"
                    key="error"
                    class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ errorText }}
                    </span>
                </div>
                <div
                    v-else-if="danger && dangerText"
                    key="danger"
                    class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ dangerText }}
                    </span>
                </div>
                <div
                    v-else-if="success"
                    key="success"
                    class="con-text-validation vs-input--text-validation"
                >
                    <span
                        class="span-text-validation span-text-validation-success vs-input--text-validation-span"
                    >
                        {{ successText }}
                    </span>
                </div>
                <div
                    v-else-if="warning"
                    key="warning"
                    class="con-text-validation span-text-validation-warning vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ warningText }}
                    </span>
                </div>
                <div
                    v-if="descriptionText"
                    key="description"
                    class="con-text-validation span-text-validation vs-input--text-validation-span"
                >
                    <span class="span-text-validation">
                        {{ descriptionText }}
                    </span>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import _color from '@/assets/utils/color.js'
import validationMixins from '@/plugins/validation/mixins.js'

export default {
    name: 'VxInput',
    inheritAttrs: false,
    mixins: [validationMixins],
    props: {
        value: {
            default: '',
            type: [String, Number]
        },
        labelPlaceholder: {
            default: null,
            type: [String, Number]
        },
        label: {
            default: null,
            type: [String, Number]
        },
        autofocus: {
            default: false,
            type: [Boolean, String]
        },
        icon: {
            default: null,
            type: String
        },
        clearable: {
            type: Boolean,
            default: true
        },
        iconAfter: {
            default: false,
            type: [Boolean, String]
        },
        iconNoBorder: {
            default: true,
            type: Boolean
        },
        iconPack: {
            default: 'feather',
            type: String
        },
        disabled: {
            type: Boolean
        },
        color: {
            default: 'primary',
            type: String
        },
        success: {
            default: false,
            type: Boolean
        },
        danger: {
            default: false,
            type: Boolean
        },
        warning: {
            default: false,
            type: Boolean
        },
        successText: {
            default: null,
            type: String
        },
        dangerText: {
            default: null,
            type: String
        },
        warningText: {
            default: null,
            type: String
        },
        descriptionText: {
            default: null,
            type: String
        },
        size: {
            default: 'normal',
            type: String
        },
        valIconPack: {
            default: 'feather',
            type: String
        },
        valIconSuccess: {
            default: 'icon-check',
            type: String
        },
        valIconDanger: {
            default: 'icon-x',
            type: String
        },
        valIconWarning: {
            default: 'icon-alert-triangle',
            type: String
        },
        selectOnFocus: {
            type: Boolean,
            default: false
        },
        disableChromeAutocomplete: {
            type: Boolean,
            default: false
        },
        hideStateIcons: {
            type: Boolean,
            default: false
        },
        hideIconValidate: {
            type: Boolean,
            default: false
        },
        accept: {
            default: '',
            type: String
        }
    },
    inject: {
        elForm: {
            default: ''
        },
        elFormItem: {
            default: ''
        }
    },
    data: () => ({
        isFocus: false
    }),
    computed: {
        style() {
            if (this.isFocus) {
                return {
                    border: `1px solid ${_color.getColor(this.color, 1)}`
                }
            }
            return ''
        },
        styleLabel() {
            return {
                color: this.isFocus ? _color.getColor(this.color, 1) : null
            }
        },
        listeners() {
            return {
                ...this.$listeners,
                input: (evt) => {
                    this.$emit('input', evt.target.value)
                },
                focus: (evt) => {
                    if (this.disableChromeAutocomplete) {
                        // use dummy value: fuck you google
                        evt.target.setAttribute('autocomplete', 'off')
                    }
                    this.$emit('focus', evt)
                    this.changeFocus(true)
                    if (this.selectOnFocus) {
                        evt.target.select()
                    }
                },
                blur: (evt) => {
                    this.$emit('blur', evt)
                    this.changeFocus(false)
                }
            }
        },
        isValue() {
            return this.labelPlaceholder ? true : !this.value
        },
        getIcon() {
            return this.hasValidationErrors || this.danger
                ? this.valIconDanger
                : this.warning
                ? this.valIconWarning
                : this.success
                ? this.valIconSuccess
                : ''
        }
    },
    methods: {
        clear() {
            this.$emit('input', null)
            this.$emit('selectionCleared')
        },
        // animation
        changeFocus(booleanx) {
            this.$emit('focusChanged')
            this.isFocus = booleanx
        },
        beforeEnter(el) {
            el.style.height = 0
        },
        enter(el, done) {
            let h = el.scrollHeight
            el.style.height = h + 'px'
            done()
        },
        leave: function (el) {
            el.style.height = 0 + 'px'
        },
        focusInput() {
            this.$refs.vsinput.focus()
        },
        iconClick() {
            this.focusInput()
            this.$emit('icon-click')
        }
    },
    mounted() {
        if (this.autofocus) {
            this.focusInput()
        }
    }
}
</script>

<style scoped>
.validation-padding {
    height: 19px;
}

.clear-button {
    background-color: transparent;
    z-index: 50;
    position: absolute !important;
    right: 0px;
}
</style>
